import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useShallow } from "zustand/shallow";
import {
  CopyIcon,
  EyeSlashIcon,
  FolderIcon,
  GearIcon,
  LockKeyIcon,
  PushPinIcon,
  ShareFatIcon,
  TagIcon,
  TrashSimpleIcon,
} from "@phosphor-icons/react";
import debounce from "lodash/debounce";

import { useBoundStore } from "@/app/store";
import { EditorData } from "@/features/notes/types";
import { NestedAction } from "@/shared/components/ui/NestedContentNavigator";
import { Editor } from "@/features/notes/components/Editor";
import { ICON_SIZE } from "@/config/constants";
import { SetNoteTagsOption } from "@/features/notes/components/Note/SetNoteTagsOption";
import { ShareNoteOption } from "@/features/notes/components/Note/ShareNoteOption";
import { MoveNoteOption } from "@/features/notes/components/Note/MoveNoteOption";
import { FetchedNote } from "@/services/api/fetchers/notes";
import { hooks } from "@/services/api/hooks";
import { EditorLayout } from "@/features/notes/components/Editor/components/EditorLayout";
import { NoteActions } from "@/features/notes/components/Note/NoteActions";
import { isEditorEmpty } from "@/features/notes/utils/isEditorEmpty";

export function NotesCreateRoute() {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const { userId, notes, addNote, deleteNote, tempDeleteNote, updateNote } = useBoundStore(
    useShallow((state) => ({
      userId: state.user.id,
      notes: state.notes.main,
      addNote: state.notes.addNote,
      deleteNote: state.notes.deleteNote,
      tempDeleteNote: state.notes.tempDeleteNote,
      updateNote: state.notes.updateNote,
    }))
  );
  const noteIdRef = useRef<number | null>(null);
  const actionTriggeredUnmountRef = useRef<boolean>(false);
  const lastSavedDataRef = useRef<EditorData | null>(null);
  // State to hold the full details of the note, especially right after creation or if not yet in store.
  const [currentNoteDetails, setCurrentNoteDetails] = useState<FetchedNote | null>(null);
  const { data: refetchedNote, isFetchedAfterMount } = hooks.notes.useNoteById(currentNoteDetails?.id);

  const storeFetchedNote = useMemo(() => {
    if (!noteIdRef.current) return null;
    return notes.find((note) => note.id === noteIdRef.current) || null;
  }, [notes]);

  const noteForEditorAndActions = storeFetchedNote || currentNoteDetails;

  const saveNoteContent = useCallback(
    async (data: EditorData) => {
      if (!userId) {
        return;
      }

      if (lastSavedDataRef.current && lastSavedDataRef.current.html_content === data.html_content) {
        return;
      }

      if (isEditorEmpty(data)) {
        if (noteIdRef.current) {
          await deleteNote(noteIdRef.current);
          noteIdRef.current = null;
          setCurrentNoteDetails(null);
        }
        lastSavedDataRef.current = { ...data };
        return;
      }

      if (noteIdRef.current) {
        const updatedNoteData = await updateNote(
          noteIdRef.current,
          data,
          Boolean(noteForEditorAndActions?.public_uuid)
        );
        if (updatedNoteData) {
          // No need to setCurrentNoteDetails, storeFetchedNote will update
          lastSavedDataRef.current = { ...data };
        }
      } else {
        const currentFolderId = folderId === "0" ? null : Number(folderId);
        const newNoteData = await addNote({ ...data, user_id: userId, folder_id: currentFolderId });
        if (newNoteData && newNoteData.id) {
          noteIdRef.current = newNoteData.id;
          setCurrentNoteDetails({ ...newNoteData, tags: [] }); // Set details for immediate use
          lastSavedDataRef.current = { ...data };
        }
      }
    },
    [noteForEditorAndActions, addNote, deleteNote, updateNote, userId, folderId]
  );

  const debouncedSave = useMemo(() => debounce(saveNoteContent, 1000), [saveNoteContent]);

  useEffect(() => {
    return () => {
      if (actionTriggeredUnmountRef.current) {
        // If unmount was due to an action (delete, close, escape), cancel pending saves
        // as those actions handle saving explicitly or don't require it.
        debouncedSave.cancel();
      } else {
        // Otherwise (e.g., browser back button), flush pending changes to save them.
        debouncedSave.flush();
      }
    };
  }, [debouncedSave]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        debouncedSave.flush(); // Save any pending changes.
        actionTriggeredUnmountRef.current = true; // Mark that an action triggered navigation.
        navigate(-1); // Navigate back.
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup: remove event listener on unmount.
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate, debouncedSave]);

  const handleEditorChange = useCallback(
    (data: EditorData) => {
      debouncedSave(data);
    },
    [debouncedSave]
  );

  const handleClose = useCallback(() => {
    debouncedSave.flush(); // Save any pending changes.
    actionTriggeredUnmountRef.current = true; // Mark that an action triggered navigation.
    navigate(-1); // Navigate back.
  }, [navigate, debouncedSave]);

  const handleDelete = useCallback(() => {
    debouncedSave.cancel();
    if (noteIdRef.current) {
      tempDeleteNote(noteIdRef.current);
      noteIdRef.current = null;
      setCurrentNoteDetails(null); // Clear note details on delete
    }
    actionTriggeredUnmountRef.current = true;
    navigate(-2);
  }, [tempDeleteNote, navigate, debouncedSave]);

  // const contentCreationActions = useCallback((): NestedAction[] => {
  //   const actions: NestedAction[] = [];

  //   if (!noteForEditorAndActions?.locked) {
  //     actions.push({
  //       content: <SpeechToText noteHasContent={!!noteForEditorAndActions?.text_content} />,
  //       key: "speech-to-text",
  //       label: "Voice note",
  //       icon: <MicrophoneIcon size={ICON_SIZE.sm} />,
  //     });
  //   }

  //   return actions;
  // }, [noteForEditorAndActions]);

  const actions = useCallback((): NestedAction[] => {
    const baseActions: NestedAction[] = [
      {
        key: "settings",
        label: "Settings",
        icon: <GearIcon size={ICON_SIZE.md} />,
        run: () => {
          navigate("settings", { replace: true });
        },
      },
    ];

    // Delete action is available if the note has been saved at least once
    if (noteIdRef.current && noteForEditorAndActions) {
      baseActions.unshift({
        key: "delete",
        label: "Delete",
        icon: <TrashSimpleIcon size={ICON_SIZE.md} />,
        run: handleDelete,
      });
    }

    // More actions if the note exists (has an ID) and user is logged in
    if (noteForEditorAndActions && noteForEditorAndActions.id && userId) {
      const noteSpecificActions: NestedAction[] = [
        {
          key: "duplicate",
          label: "Duplicate",
          icon: <CopyIcon size={ICON_SIZE.sm} />,
          run: async () => {
            await debouncedSave.flush();
            if (
              noteForEditorAndActions.html_content !== undefined &&
              noteForEditorAndActions.text_content !== undefined
            ) {
              const currentFolderId = folderId === "0" ? null : Number(folderId);
              addNote({
                html_content: noteForEditorAndActions.html_content,
                text_content: noteForEditorAndActions.text_content,
                user_id: userId,
                folder_id: currentFolderId,
              });

              navigate(-2);
            }
          },
        },
        {
          key: "tags",
          label: "Tags",
          icon: <TagIcon size={ICON_SIZE.md} />,
          content: <SetNoteTagsOption noteId={noteForEditorAndActions.id} />,
        },
        {
          key: "share",
          label: "Share",
          icon: <ShareFatIcon size={ICON_SIZE.md} weight={noteForEditorAndActions.public_uuid ? "fill" : "regular"} />,
          content: <ShareNoteOption noteId={noteForEditorAndActions.id} />,
        },
        {
          key: "move",
          label: "Move",
          icon: <FolderIcon size={ICON_SIZE.md} />,
          content: <MoveNoteOption note={noteForEditorAndActions} />,
        },
        {
          key: "pin",
          label: noteForEditorAndActions.pinned ? "Unpin" : "Pin",
          icon: <PushPinIcon size={ICON_SIZE.md} weight={noteForEditorAndActions.pinned ? "fill" : "regular"} />,
          run: () => {
            if (noteForEditorAndActions.id) {
              updateNote(
                noteForEditorAndActions.id,
                { pinned: !noteForEditorAndActions.pinned },
                Boolean(noteForEditorAndActions.public_uuid)
              );
              navigate(-1);
            }
          },
        },
        {
          key: "lock",
          label: noteForEditorAndActions.locked ? "Unlock" : "Lock",
          icon: <LockKeyIcon size={ICON_SIZE.sm} weight={noteForEditorAndActions.locked ? "fill" : "regular"} />,
          run: () => {
            if (noteForEditorAndActions.id) {
              updateNote(
                noteForEditorAndActions.id,
                { locked: !noteForEditorAndActions.locked },
                Boolean(noteForEditorAndActions.public_uuid)
              );
              navigate(-1);
            }
          },
        },
        {
          key: "mask",
          label: noteForEditorAndActions.masked ? "Unmask" : "Mask",
          icon: noteForEditorAndActions.masked ? (
            <EyeSlashIcon size={ICON_SIZE.sm} weight="fill" />
          ) : (
            <EyeSlashIcon size={ICON_SIZE.sm} />
          ),
          run: () => {
            if (noteForEditorAndActions.id) {
              updateNote(
                noteForEditorAndActions.id,
                { masked: !noteForEditorAndActions.masked },
                Boolean(noteForEditorAndActions.public_uuid)
              );
              navigate(-1);
            }
          },
        },
      ];
      return [...noteSpecificActions, ...baseActions];
    }

    return baseActions;
  }, [noteForEditorAndActions, handleDelete, userId, addNote, updateNote, debouncedSave, navigate, folderId]);

  return (
    <EditorLayout
      isEditable={!noteForEditorAndActions?.locked}
      navbar={{
        actions: [<NoteActions actions={actions} />],
        onBack: handleClose,
      }}
    >
      <Editor
        isEditable={!noteForEditorAndActions?.locked}
        note={noteForEditorAndActions}
        refetchedNote={isFetchedAfterMount ? (refetchedNote ?? null) : null}
        onChange={handleEditorChange}
      />
    </EditorLayout>
  );
}
